/**
 * `gamification-core` — CONFIG del dominio bible-strong (lectura de la Escritura).
 *
 * Retrofit OPT-IN y NO punitivo: la gamificación premia el ENGAGEMENT con la
 * Palabra (constancia de lectura, notas, subrayados, marcadores) como aliento,
 * nunca como presión. Ligas privadas por defecto (§leagues). El motor es
 * agnóstico; aquí solo declaramos las reglas del dominio.
 *
 * SEÑAL DE LECTURA (racha): esta app NO tiene un substrato de `activity` ni un
 * registro explícito de "capítulo leído". La racha se DERIVA como PROXY de la
 * actividad de anotación disponible LOCALMENTE — la unión de notas, subrayados
 * y marcadores (`bible_note` + `bible_highlight` + `bible_verse_highlight` +
 * `bible_bookmark`) — donde cada día local con al menos una anotación cuenta
 * como un día de lectura. Es NO invasivo: no añade tablas, no toca sync rules ni
 * el servidor. Ver `derive.ts`.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Tipos de evento de dominio (derivados de las anotaciones locales). */
export const BSO_EVENTS = {
  noteCreated: "note.created",
  highlightCreated: "highlight.created",
  bookmarkCreated: "bookmark.created",
  planDayCompleted: "plan-day.completed",
} as const;

/** Línea de racha: días con alguna actividad de lectura/anotación (constancia). */
export const READING_STREAK_LINE = "reading";

/** Bonus XP de los badges de constancia (definidos aquí para reusarlos en tests). */
export const DEVOTED_READER_BONUS_XP = 50;
export const FAITHFUL_MONTH_BONUS_XP = 100;
/** Bonus de la quest diaria (una vez por día al escribir una nota). */
export const DAILY_READING_BONUS_XP = 15;

export const bsoGamificationConfig: GamificationConfig = {
  xp: {
    [BSO_EVENTS.noteCreated]: 10,
    [BSO_EVENTS.highlightCreated]: 5,
    [BSO_EVENTS.bookmarkCreated]: 3,
    [BSO_EVENTS.planDayCompleted]: 5,
  },
  // CUALQUIER anotación marca su día como día de lectura → extiende la racha.
  streakLines: {
    [BSO_EVENTS.noteCreated]: [READING_STREAK_LINE],
    [BSO_EVENTS.highlightCreated]: [READING_STREAK_LINE],
    [BSO_EVENTS.bookmarkCreated]: [READING_STREAK_LINE],
    [BSO_EVENTS.planDayCompleted]: [READING_STREAK_LINE],
  },
  counters: {
    [BSO_EVENTS.noteCreated]: ["notes"],
    [BSO_EVENTS.highlightCreated]: ["highlights"],
    [BSO_EVENTS.bookmarkCreated]: ["bookmarks"],
    [BSO_EVENTS.planDayCompleted]: ["planDays"],
  },
  badges: [
    {
      id: "first-note",
      title: "First note",
      description: "Write your first study note.",
      when: counterAtLeast("notes", 1),
    },
    {
      id: "devoted-reader",
      title: "Devoted reader",
      description: "Read or annotate seven days in a row.",
      when: streakAtLeast(READING_STREAK_LINE, 7),
      bonusXp: DEVOTED_READER_BONUS_XP,
    },
    {
      id: "faithful-month",
      title: "Faithful month",
      description: "Keep a thirty-day reading streak.",
      when: streakAtLeast(READING_STREAK_LINE, 30),
      bonusXp: FAITHFUL_MONTH_BONUS_XP,
    },
    {
      id: "scholar",
      title: "Scholar",
      description: "Write fifty study notes.",
      when: counterAtLeast("notes", 50),
    },
  ],
  quests: [
    {
      id: "daily-reading",
      title: "Spend time in the Word today",
      period: "daily",
      goals: [{ metric: BSO_EVENTS.noteCreated, target: 1 }],
      bonusXp: DAILY_READING_BONUS_XP,
    },
  ],
};
